import {
  Bot,
  ChevronsDown,
  ChevronsUp,
  Crown,
  Flag,
  LogIn,
  LogOut,
  UserMinus,
  type LucideIcon,
} from "lucide-react";

import { Pill } from "../../components/Pill/Pill";
import { formatMessageTime } from "./chatFeedFormat";
import type { MemberEventAction, MemberEventMessage } from "./types";
import styles from "./GuildChatFeed.module.css";

/** The lucide icon each roster change is drawn with — mockup section 2. */
const EVENT_ICON: Record<MemberEventAction, LucideIcon> = {
  created: Flag,
  joined: LogIn,
  left: LogOut,
  kicked: UserMinus,
  promoted: ChevronsUp,
  demoted: ChevronsDown,
  leader_changed: Crown,
};

/**
 * The palette modifier each change carries, mirroring the Pill variants:
 * joined → success, kicked → danger, promoted → info, leader_changed → warn.
 * Leaving and being demoted keep the row's neutral badge — neither is an
 * incident, and there is nothing for a colour to shout about.
 *
 * Founding the guild takes the project accent instead of any of the four, and
 * for the same kind of reason the other two take none: it is not an incident in
 * either direction. It is the one row a feed can never hold two of, which is
 * what the odd colour out is saying.
 */
const EVENT_CLASS: Record<MemberEventAction, string> = {
  created: styles.evCreated,
  joined: styles.evJoined,
  left: "",
  kicked: styles.evKicked,
  promoted: styles.evPromoted,
  demoted: "",
  leader_changed: styles.evLeader,
};

/**
 * One roster change in the feed — mockup `guild-chat-member-events.html`,
 * section 2: an icon in the gutter where a message keeps its avatar, one line
 * of text, and the time.
 *
 * It carries NO Delete button, unlike the two message rows. Moderation removes
 * what somebody said; "X kicked Y" is a fact, and erasing it would be hiding
 * the evidence. Such a row leaves only with the retention window that trims the
 * rest of the chat.
 *
 * The names are read from the live join on `players`, never from `body` — the
 * body is the English sentence the game client draws, with the name of the day
 * baked into it — and they are bold text rather than links: the author of a
 * message is not clickable here either.
 */
export function MemberEventRow({
  msg,
  className,
}: {
  msg: MemberEventMessage;
  /** Extra class on the row — the stream's context panel dims it. */
  className?: string;
}) {
  const Icon = EVENT_ICON[msg.event_action];
  return (
    <div
      className={[styles.evRow, EVENT_CLASS[msg.event_action], className]
        .filter(Boolean)
        .join(" ")}
      data-testid="member-event-row"
    >
      <span className={styles.evGutter}>
        <span className={styles.evBadge}>
          <Icon size={13} aria-hidden="true" />
        </span>
      </span>
      <span className={styles.evLine}>
        <MemberEventSentence msg={msg} />
        {msg.event_player_is_bot && (
          <Pill size="sm">
            <span className={styles.botMark}>
              <Bot size={10} aria-hidden="true" />
              Bot
            </span>
          </Pill>
        )}
      </span>
      <span className={styles.evTime}>{formatMessageTime(msg.created_at)}</span>
    </div>
  );
}

/**
 * What the row says, per action.
 *
 * The author is named only where an author exists and the sentence is about
 * something done TO somebody: nobody joins or leaves on another player's
 * behalf, and the leader's seat reads the same whether it was handed over or
 * inherited. An absent author on a kick, a promotion or a demotion means the
 * order came from this panel — the same convention `blocked_by` follows on the
 * Blocked list, where NULL is a moderator and the activity log says which one.
 */
function MemberEventSentence({ msg }: { msg: MemberEventMessage }) {
  const player = <EventName name={msg.event_player_name} />;
  const by = msg.event_actor_id ? (
    <>
      by <EventName name={msg.event_actor_name} />
    </>
  ) : (
    <>by a moderator</>
  );
  switch (msg.event_action) {
    case "created":
      // No author, ever: a guild is not founded on somebody else's behalf, and
      // the server writes event_actor_id NULL for exactly that reason.
      return <>{player} created the guild</>;
    case "joined":
      return <>{player} joined the guild</>;
    case "left":
      return <>{player} left the guild</>;
    case "kicked":
      return (
        <>
          {player} was kicked {by}
        </>
      );
    case "promoted":
      return (
        <>
          {player} was promoted to officer {by}
        </>
      );
    case "demoted":
      return (
        <>
          {player} was demoted to member {by}
        </>
      );
    case "leader_changed":
      return <>{player} is now the leader</>;
  }
}

/**
 * One name inside a member event. A name that does not resolve is drawn muted
 * rather than at full weight — the row keeps its place in the feed either way
 * (the join is a LEFT one on purpose, and deleting a player deliberately leaves
 * their chat rows behind), so "?" is a state of this row and not a name.
 *
 * The same treatment the help-request row gives its own unresolved requester,
 * because two "?" one above the other in the same card must not look like two
 * different things.
 */
function EventName({ name }: { name?: string }) {
  if (name === undefined) return <b className={styles.evNameUnknown}>?</b>;
  return <b>{name}</b>;
}
